import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { initialData, ADMIN_PWD_DEFAULT } from '../data/initialData';
import {
  useConfigSync, useTransactionsSync, useEtudiantsSync,
  useDepensesSync, useVisitesSync,
  addDepense, removeDepense, logVisite,
  saveConfig, saveAdminPwd,
  addTransaction, patchTransaction, removeTransaction,
  addEtudiant, patchEtudiant, removeEtudiant,
  exportAllData, importAllData,
} from '../hooks/useFirestore';
import { useExchangeRates } from '../hooks/useExchangeRates';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [data,        setData]          = useState(initialData);
  const [adminPwd,    setAdminPwdState] = useState(ADMIN_PWD_DEFAULT);
  const [currentUser, setCurrentUser]   = useState(null);
  const [savedAdmin,  setSavedAdmin]    = useState(null);
  const [toast,       setToast]         = useState({ msg: '', type: 'ok', visible: false });
  const [fbReady,     setFbReady]       = useState(false);

  // ── Panier boutique (KotosShop) — persisté localement ────
  const [cart,     setCart]     = useState([]);
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('kotos_cart');
    if (saved) {
      try { setCart(JSON.parse(saved)); }
      catch (e) { localStorage.removeItem('kotos_cart'); }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('kotos_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((produit, qty = 1) => {
    setCart(prev => {
      const existing = prev.find(it => it.id === produit.id);
      if (existing) {
        return prev.map(it => it.id === produit.id ? { ...it, qty: it.qty + qty } : it);
      }
      return [...prev, {
        id: produit.id, name: produit.name, icon: produit.icon, iconImage: produit.iconImage || '',
        unitPrice: produit.priceFcfa, unit: produit.unit, qty,
      }];
    });
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart(prev => prev.filter(it => it.id !== id));
  }, []);

  const updateCartQty = useCallback((id, qty) => {
    setCart(prev => prev.map(it => it.id === id ? { ...it, qty: Math.max(1, qty) } : it));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = cart.reduce((s, it) => s + it.qty, 0);
  const cartTotal = cart.reduce((s, it) => s + it.qty * it.unitPrice, 0);

  // ── Restaurer session au refresh ─────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem('kotos_user');
    if (saved) {
      try { setCurrentUser(JSON.parse(saved)); }
      catch (e) { localStorage.removeItem('kotos_user'); }
    }

    // ── Restaurer aussi l'état "mode visiteur" ────────────
    // Avant ce correctif, `savedAdmin` n'existait qu'en mémoire React :
    // un refresh de page pendant que l'admin prévisualisait le site en
    // mode "visiteur" perdait cette information, et le bouton
    // "Retourner en mode Admin" ne faisait plus rien (voir exitVisitorMode
    // ci-dessous). On restaure maintenant `savedAdmin` depuis localStorage
    // pour que ce flux survive à un rechargement de page.
    const savedAdminRaw = localStorage.getItem('kotos_saved_admin');
    if (savedAdminRaw) {
      try { setSavedAdmin(JSON.parse(savedAdminRaw)); }
      catch (e) { localStorage.removeItem('kotos_saved_admin'); }
    }
  }, []);

  // ── Synchro Firebase temps réel ──────────────────────────
  useConfigSync(setData, setAdminPwdState, initialData, ADMIN_PWD_DEFAULT);
  useTransactionsSync(setData);
  useEtudiantsSync(setData);
  useDepensesSync(setData);
  useVisitesSync(setData);

  useEffect(() => {
    const t = setTimeout(() => setFbReady(true), 1200);
    return () => clearTimeout(t);
  }, []);

  // ── Toast ────────────────────────────────────────────────
  function showToast(msg, type = 'ok') {
    setToast({ msg, type, visible: true });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 2800);
  }

  // ── updateData : local + Firebase ───────────────────────
  const updateData = useCallback(async (patch) => {
    setData(prev => ({ ...prev, ...patch }));
    try { await saveConfig(patch); }
    catch (e) { console.warn('FB updateData:', e.message); }
  }, []);

  // ── Taux de change automatiques — APRÈS updateData ───────
  useExchangeRates(data, updateData);

  // ── Changer mot de passe admin ───────────────────────────
  const setAdminPwd = useCallback(async (newPwd) => {
    setAdminPwdState(newPwd);
    try { await saveAdminPwd(newPwd); }
    catch (e) { console.warn('FB setAdminPwd:', e.message); }
  }, []);

  // ── Login ────────────────────────────────────────────────
  function login(role, pwd, workerId = null) {
    if (role === 'admin') {
      if (pwd !== adminPwd) return false;
      const user = { name: 'Administrateur', role: 'admin', id: 0 };
      setCurrentUser(user);
      localStorage.setItem('kotos_user', JSON.stringify(user));
      // ── Tag visite interne ──
      localStorage.setItem('kotos_session_type', 'interne');
      localStorage.setItem('kotos_session_name', 'Administrateur');
      localStorage.setItem('kotos_session_role', 'admin');
      return true;
    }
    const worker = data.workers.find(w => w.id === workerId);
    if (worker && pwd === worker.pwd) {
      const user = { name: worker.name, role: worker.role, id: worker.id };
      setCurrentUser(user);
      localStorage.setItem('kotos_user', JSON.stringify(user));
      // ── Tag visite interne ──
      localStorage.setItem('kotos_session_type', 'interne');
      localStorage.setItem('kotos_session_name', worker.name);
      localStorage.setItem('kotos_session_role', worker.role);
      return true;
    }
    return false;
  }

  function logout() {
    setCurrentUser(null);
    setSavedAdmin(null);
    localStorage.removeItem('kotos_user');
    localStorage.removeItem('kotos_saved_admin'); // ← nettoyage ajouté
    // ── Supprimer tag interne ──
    localStorage.removeItem('kotos_session_type');
    localStorage.removeItem('kotos_session_name');
    localStorage.removeItem('kotos_session_role');
  }

  function enterVisitorMode() {
    if (currentUser?.role === 'admin') {
      setSavedAdmin(currentUser);
      // ← AJOUT : on persiste l'admin sauvegardé, pas seulement en état React,
      // pour pouvoir revenir en admin même après un refresh de page.
      localStorage.setItem('kotos_saved_admin', JSON.stringify(currentUser));
      setCurrentUser(null);
      localStorage.removeItem('kotos_user');
      // En mode visiteur → on simule un client
      localStorage.setItem('kotos_session_type', 'visiteur_admin');
    }
  }

  function exitVisitorMode() {
    // ← Fallback sur localStorage si l'état React a été perdu (refresh)
    const restored = savedAdmin || JSON.parse(localStorage.getItem('kotos_saved_admin') || 'null');
    if (restored) {
      setCurrentUser(restored);
      localStorage.setItem('kotos_user', JSON.stringify(restored));
      localStorage.setItem('kotos_session_type', 'interne');
      localStorage.setItem('kotos_session_name', restored.name);
      localStorage.setItem('kotos_session_role', restored.role);
      localStorage.removeItem('kotos_saved_admin'); // ← nettoyage
      setSavedAdmin(null);
    }
  }

  const isVisitorMode = savedAdmin !== null && currentUser === null;

  // ── Créer facture / devis / commande → Firebase ─────────
  const saveTransaction = useCallback(async (type, lines, meta, totals) => {
    const now = new Date();
    const txData = {
      ...meta,
      id:         Date.now(),
      type,       num: meta.num,
      date:       meta.date || now.toISOString().slice(0, 10),
      dateStr:    now.toLocaleDateString('fr-FR'),
      valid:      meta.valid || 30,
      client:     meta.cnom   || '—',
      cnom:       meta.cnom   || '',
      ctel:       meta.ctel   || '',
      cemail:     meta.cemail || '',
      cadr:       meta.cadr   || '',
      worker:     currentUser ? currentUser.name : '—',
      workerId:   currentUser ? currentUser.id   : null,
      workerRole: currentUser ? currentUser.role  : '—',
      lines,      notes: meta.notes || '',
      total:      totals.total,       sub:      totals.sub,
      remisePct:  totals.remisePct || 0, remiseAmt: totals.remiseAmt || 0,
      tvaPct:     totals.tvaPct    || 0, tvaAmt:    totals.tvaAmt    || 0,
      acompte:    totals.acompte   || 0, restant:   totals.restant   || totals.total,
      statut: 'nouveau', classeFolder: '', adminNote: '',
    };
    try {
      await addTransaction(txData);
      showToast('Document sauvegardé dans le cloud ☁️', 'ok');
    } catch (e) {
      setData(prev => ({ ...prev, transactions: [txData, ...prev.transactions] }));
      showToast('Sauvegardé localement (hors-ligne)', 'warn');
    }
    return txData.id;
  }, [currentUser]);

  // ── Supprimer transaction ────────────────────────────────
  const deleteTransaction = useCallback(async (id) => {
    const tx = data.transactions.find(t => t.id === id);
    setData(prev => ({ ...prev, transactions: prev.transactions.filter(t => t.id !== id) }));
    try { if (tx?._docId) await removeTransaction(tx._docId); }
    catch (e) { console.warn('FB deleteTransaction:', e.message); }
  }, [data.transactions]);

  // ── Mettre à jour transaction ────────────────────────────
  const updateTransaction = useCallback(async (id, patch) => {
    const tx = data.transactions.find(t => t.id === id);
    setData(prev => ({
      ...prev,
      transactions: prev.transactions.map(t => t.id === id ? { ...t, ...patch } : t),
    }));
    try { if (tx?._docId) await patchTransaction(tx._docId, patch); }
    catch (e) { console.warn('FB updateTransaction:', e.message); }
  }, [data.transactions]);

  // ── Étudiants Firebase ────────────────────────────────────
  const saveEtudiant = useCallback(async (etData) => {
    try { await addEtudiant(etData); showToast('Étudiant sauvegardé ☁️', 'ok'); }
    catch (e) { setData(prev => ({ ...prev, etudiants: [...(prev.etudiants||[]), etData] })); }
  }, []);

  const updateEtudiant = useCallback(async (id, patch) => {
    const et = (data.etudiants||[]).find(e => e.id === id);
    setData(prev => ({ ...prev, etudiants: (prev.etudiants||[]).map(e => e.id===id ? {...e,...patch} : e) }));
    try { if (et?._docId) await patchEtudiant(et._docId, patch); }
    catch (e) { console.warn('FB updateEtudiant:', e.message); }
  }, [data.etudiants]);

  const deleteEtudiant = useCallback(async (id) => {
    const et = (data.etudiants||[]).find(e => e.id === id);
    setData(prev => ({ ...prev, etudiants: (prev.etudiants||[]).filter(e => e.id !== id) }));
    try { if (et?._docId) await removeEtudiant(et._docId); }
    catch (e) { console.warn('FB removeEtudiant:', e.message); }
  }, [data.etudiants]);

  // ── Dépenses Firebase (Admin → Finances) ─────────────────
  const saveDepense = useCallback(async (depData) => {
    const entry = { id: Date.now(), ...depData };
    setData(prev => ({ ...prev, depenses: [entry, ...(prev.depenses||[])] }));
    try { await addDepense(entry); }
    catch (e) { console.warn('FB addDepense:', e.message); }
  }, []);

  const deleteDepense = useCallback(async (id) => {
    const dep = (data.depenses||[]).find(d => d.id === id);
    setData(prev => ({ ...prev, depenses: (prev.depenses||[]).filter(d => d.id !== id) }));
    try { if (dep?._docId) await removeDepense(dep._docId); }
    catch (e) { console.warn('FB removeDepense:', e.message); }
  }, [data.depenses]);

  // ── Suivi de visites (anonyme, agrégé) ───────────────────
  const trackVisite = useCallback(async (payload) => {
    try { await logVisite({ ts: Date.now(), dateStr: new Date().toISOString().slice(0, 10), ...payload }); }
    catch (e) { console.warn('FB logVisite:', e.message); }
  }, []);

  // ── Export / Import ──────────────────────────────────────
  const exportData = useCallback(async () => {
    try {
      const allData = await exportAllData();
      const blob = new Blob([JSON.stringify(allData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      a.download = `kotos_backup_${new Date().toISOString().slice(0,10)}.json`;
      a.click(); URL.revokeObjectURL(url);
      showToast('Sauvegarde téléchargée ✓', 'ok');
    } catch (e) { showToast('Erreur export : ' + e.message, 'error'); }
  }, []);

  const importData = useCallback(async (jsonString) => {
    try {
      await importAllData(JSON.parse(jsonString));
      showToast('Données restaurées ✓', 'ok');
    } catch (e) { showToast('Erreur import : ' + e.message, 'error'); }
  }, []);

  return (
    <AppContext.Provider value={{
      data, updateData, adminPwd, setAdminPwd,
      currentUser, login, logout,
      savedAdmin, enterVisitorMode, exitVisitorMode, isVisitorMode,
      toast, showToast, fbReady,
      saveTransaction, deleteTransaction, updateTransaction,
      saveEtudiant, updateEtudiant, deleteEtudiant,
      saveDepense, deleteDepense, trackVisite,
      exportData, importData,
      cart, showCart, setShowCart,
      addToCart, removeFromCart, updateCartQty, clearCart,
      cartCount, cartTotal,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() { return useContext(AppContext); }